<template>
  <b-modal
    :active.sync="show"
    trap-focus
    aria-role="dialog"
    scroll="keep"
    :width="1000"
    aria-modal
    has-modal-card
    @close="closeModal"
  >
    <ValidationObserver ref="observer" v-slot="{ passes }">
      <form @submit.prevent="passes(save)">
        <div class="modal-card" style="width: 1000px">
          <header class="modal-card-head">
            <p class="modal-card-title">Edit Sequence</p>
          </header>
          <section
            class="modal-card-body"
            style="max-height: 600px; overflow-y: auto"
          >
            <label class="has-text-weight-bold">Sequence Items</label>
            <div
              v-for="item in scheduleItems"
              :key="item.id"
              style="margin-bottom: 8px; padding: 8px 0; border-bottom: 1px solid #f0f0f0;"
            >
              <div
                v-if="!item.editing"
                style="display: flex; align-items: center; gap: 10px;"
              >
                <b-checkbox v-model="item.selected" style="flex-shrink: 0;" />
                <span style="flex: 1; min-width: 0;">
                  {{ getTitle(item) }} - {{ formatTime(item.start_time) }} -
                  {{ formatTime(item.end_time) }}
                </span>
                <a
                  href=""
                  style="flex-shrink: 0;"
                  @click.prevent="toggleEdit(item)"
                >
                  <b-icon icon="pencil" size="is-small"></b-icon>
                </a>
                <a
                  href=""
                  style="flex-shrink: 0;"
                  @click.prevent="removeItem(item)"
                >
                  <b-icon icon="close" size="is-small"></b-icon>
                </a>
              </div>
              <div
                v-else
                style="display: flex; flex-direction: column; gap: 10px;"
              >
                <div style="display: flex; align-items: center; gap: 10px;">
                  <b-checkbox v-model="item.selected" style="flex-shrink: 0;" />
                  <span style="flex: 1; min-width: 0;">
                    <strong>{{ getTitle(item) }}</strong>
                  </span>
                  <a
                    href=""
                    style="flex-shrink: 0;"
                    @click.prevent="saveItemEdit(item)"
                  >
                    <b-icon
                      icon="check"
                      size="is-small"
                      type="is-success"
                    ></b-icon>
                  </a>
                  <a
                    href=""
                    style="flex-shrink: 0;"
                    @click.prevent="cancelEdit(item)"
                  >
                    <b-icon icon="close" size="is-small"></b-icon>
                  </a>
                </div>
                <div style="display: flex; gap: 10px; padding-left: 35px;">
                  <div style="flex: 1;">
                    <BaseDateTimePicker
                      v-model="item.start_time"
                      label="Start Time"
                      @input="updateItemEndTime(item)"
                    />
                  </div>
                  <div style="flex: 1;">
                    <BaseDateTimePicker
                      :key="item.id + '-' + item.end_time"
                      v-model="item.end_time"
                      label="End Time"
                      :min-datetime="item.start_time"
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>
          <footer class="modal-card-foot" style="display: block">
            <button class="button" type="button" @click="closeModal">
              Cancel
            </button>
            <button
              v-if="hasChanges"
              class="button is-success"
              type="button"
              :loading="loading"
              @click="saveChanges()"
            >
              Save Changes
            </button>
            <button
              class="button is-primary"
              type="button"
              @click="changeTitle()"
            >
              Change Title
            </button>
            <button
              class="button is-primary"
              type="button"
              @click="copySequence()"
            >
              Copy
            </button>
            <button
              class="button is-info is-pulled-right has-margin-right-5"
              :loading="loading"
              @click.prevent="confirmDelete"
            >
              Delete Sequence
            </button>
            <button
              v-if="anySelected"
              class="button is-info is-pulled-right has-margin-right-5"
              :loading="loading"
              @click.prevent="confirmDeleteItems"
            >
              Delete Sequence Items
            </button>
          </footer>
        </div>
      </form>
    </ValidationObserver>
    <ChangeSequenceTitleModal
      :repeats-id="repeatsId"
      :channel-id="channelId"
      :show="showChangeSequenceTitleModal"
      @close-modal="showChangeSequenceTitleModal = false"
      @refresh="
        $emit('refresh')
        showModal = false
        $emit('close-modal')
      "
    ></ChangeSequenceTitleModal>
    <CopySequenceModal
      :repeats-id="repeatsId"
      :channel-id="channelId"
      :show="showCopySequenceModal"
      @close-modal="showCopySequenceModal = false"
      @refresh="
        $emit('refresh')
        showModal = false
        $emit('close-modal')
      "
    ></CopySequenceModal>
  </b-modal>
</template>

<script>
import { sortBy } from 'lodash'
import ChangeSequenceTitleModal from '@/components/ChangeSequenceTitleModal'
import CopySequenceModal from '@/components/CopySequenceModal'
import BaseDateTimePicker from '@/components/BaseDateTimePicker'
import BaseSelect from '@/components/BaseSelect'
import MediaChannelApi from '@/api/mediaChannels'
import EpisodesApi from '@/api/episodes'
import ScheduleApi from '@/api/schedules'
import Filters from '@/mixins/filters'
import Utils from '@/mixins/utils'
import { format } from 'date-fns'
import addMinutes from 'date-fns/addMinutes'

export default {
  name: 'EditSequenceModal',
  components: {
    BaseDateTimePicker,
    BaseSelect,
    ChangeSequenceTitleModal,
    CopySequenceModal
  },
  mixins: [Filters, Utils],
  props: {
    show: {
      type: Boolean,
      default: false
    },
    channelId: {
      type: Number,
      default: null
    },
    repeatsId: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      loading: false,
      initialized: false,
      showModal: this.show,
      scheduleItems: [],
      originalScheduleItems: [],
      repeatsInfo: {},
      showChangeSequenceTitleModal: false,
      showCopySequenceModal: false
    }
  },
  computed: {
    anySelected() {
      if (this.scheduleItems && this.scheduleItems.length > 0) {
        return this.scheduleItems.find(item => item.selected)
      }
      return false
    },
    hasChanges() {
      return this.scheduleItems.some(item => {
        const original = this.originalScheduleItems.find(o => o.id === item.id)
        if (!original) return false
        return (
          item.start_time !== original.start_time ||
          item.end_time !== original.end_time ||
          item.editing
        )
      })
    }
  },
  watch: {},
  async created() {
    this.repeatsInfo = await ScheduleApi.fetchSequence(this.repeatsId)
    this.scheduleItems = await ScheduleApi.fetchSequenceItems(this.repeatsId)
    this.scheduleItems.forEach(item => {
      if (item.mediadata) {
        item.mediadata = JSON.parse(item.mediadata)
      }
      // Initialize editing property reactively
      this.$set(item, 'editing', false)
      this.$set(item, 'selected', false)
    })
    // Store original values for comparison
    this.originalScheduleItems = JSON.parse(JSON.stringify(this.scheduleItems))
  },
  methods: {
    removeItem(item) {
      let vm = this
      this.$buefy.dialog.confirm({
        message: 'Are you sure you want to delete this item?',
        onConfirm: async () => {
          vm.loading = true
          vm.scheduleItems = vm.scheduleItems.filter(i => i.id !== item.id)
          await ScheduleApi.remove(item)
          vm.$buefy.toast.open({
            message: 'Item removed successfully!',
            position: 'is-top',
            type: 'is-success'
          })
          vm.loading = false
          // Refresh calendar after delete
          vm.$emit('refresh')
          // If all items deleted, close the modal
          if (vm.scheduleItems.length === 0) {
            vm.closeModal()
          }
        }
      })
    },
    copySequence() {
      this.showCopySequenceModal = true
    },
    changeTitle() {
      this.showChangeSequenceTitleModal = true
    },
    getTitle(item) {
      if (item.media_type === 'movie') {
        return item.media_title
      } else {
        return `${item.media_title} S${item.mediadata.seriesNo} E${item.episode_number}`
      }
    },
    formatTime(date) {
      if (!date) return ''
      // Parse as local time without timezone conversion
      // Assumes date is in format: "2026-01-26T00:00:00.000Z" or "2026-01-26 00:00:00"
      const dateStr = String(date)
      const parts = dateStr.replace('T', ' ').replace('Z', '').replace(/\.\d+/, '').split(/[-\s:]/)
      if (parts.length >= 5) {
        // Create date in local timezone without conversion
        const localDate = new Date(
          parseInt(parts[0]), // year
          parseInt(parts[1]) - 1, // month (0-indexed)
          parseInt(parts[2]), // day
          parseInt(parts[3]), // hour
          parseInt(parts[4])  // minute
        )
        return format(localDate, 'dd/MM/yyyy HH:mm')
      }
      // Fallback to default parsing
      return format(new Date(date), 'dd/MM/yyyy HH:mm')
    },
    save() {},
    confirmDeleteItems() {
      let items = this.scheduleItems.filter(item => item.selected)
      this.$buefy.dialog.confirm({
        message: `Are you sure you want to delete the ${items.length} items?`,
        onConfirm: async () => {
          this.loading = true
          await ScheduleApi.removeMultiple(items.map(item => item.id))
          this.$buefy.toast.open({
            message: 'Items removed successfully!',
            position: 'is-top',
            type: 'is-success'
          })
          this.scheduleItems = this.scheduleItems.filter(item => !item.selected)
          this.loading = false
          // Refresh calendar after delete
          this.$emit('refresh')
          // If all items deleted, close the modal
          if (this.scheduleItems.length === 0) {
            this.closeModal()
          }
        }
      })
    },
    confirmDelete() {
      this.$buefy.dialog.confirm({
        message: 'Are you sure you want to delete this sequence?',
        onConfirm: async () => {
          this.loading = true
          await ScheduleApi.deleteSequence(this.repeatsId)
          this.$buefy.toast.open({
            message: 'Sequence removed successfully!',
            position: 'is-top',
            type: 'is-success'
          })
          this.loading = false
          this.$emit('refresh')
          this.closeModal()
        }
      })
    },
    closeModal() {
      this.showModal = false
      this.$emit('close-modal')
    },
    toggleEdit(item) {
      // Store original values when entering edit mode
      if (!item.editing) {
        item._original_start_time = item.start_time
        item._original_end_time = item.end_time
      }
      // Use $set to ensure Vue reactivity
      this.$set(item, 'editing', true)
    },
    async saveItemEdit(item) {
      this.loading = true
      try {
        // Save individual item
        await ScheduleApi.update({
          id: item.id,
          start_time: item.start_time,
          end_time: item.end_time,
          media_id: item.media_id,
          episode_id: item.episode_id,
          channel_id: this.channelId,
          repeats_id: this.repeatsId
        })

        this.$set(item, 'editing', false)
        delete item._original_start_time
        delete item._original_end_time

        // Update original values
        const originalIndex = this.originalScheduleItems.findIndex(
          o => o.id === item.id
        )
        if (originalIndex !== -1) {
          this.originalScheduleItems[originalIndex].start_time = item.start_time
          this.originalScheduleItems[originalIndex].end_time = item.end_time
        }

        this.$buefy.toast.open({
          message: 'Item updated successfully!',
          position: 'is-top',
          type: 'is-success'
        })

        // Refresh the calendar
        this.$emit('refresh')
      } catch (error) {
        this.$buefy.toast.open({
          message: 'Failed to save item',
          position: 'is-top',
          type: 'is-danger'
        })
      } finally {
        this.loading = false
      }
    },
    cancelEdit(item) {
      // Restore original values
      if (item._original_start_time) {
        item.start_time = item._original_start_time
      }
      if (item._original_end_time) {
        item.end_time = item._original_end_time
      }
      this.$set(item, 'editing', false)
      delete item._original_start_time
      delete item._original_end_time
    },
    updateItemEndTime(item) {
      // Auto-calculate end time based on duration
      const startTime = new Date(item.start_time)
      let duration = null

      // Try to get duration from various sources
      // 1. Episode data (for series)
      if (item.episode_data) {
        try {
          const episodeData = typeof item.episode_data === 'string'
            ? JSON.parse(item.episode_data)
            : item.episode_data
          if (episodeData && episodeData.episodeLength) {
            duration = parseInt(episodeData.episodeLength, 10)
          }
        } catch (e) {
          console.error('Error parsing episode_data:', e)
        }
      }

      // 2. Media data episodeLength (for series via mediadata)
      if (!duration && item.mediadata && item.mediadata.episodeLength) {
        duration = parseInt(item.mediadata.episodeLength, 10)
      }

      // 3. Media data mediaLength (for movies)
      if (!duration && item.mediadata && item.mediadata.mediaLength) {
        duration = parseInt(item.mediadata.mediaLength, 10)
      }

      // 4. Calculate from original start/end time difference
      if (!duration && item._original_start_time && item._original_end_time) {
        const originalStart = new Date(item._original_start_time)
        const originalEnd = new Date(item._original_end_time)
        duration = Math.round((originalEnd - originalStart) / (1000 * 60)) // Convert ms to minutes
      }

      if (duration && duration > 0) {
        // Use $set to ensure Vue reactivity
        this.$set(item, 'end_time', addMinutes(startTime, duration))
      }
    },
    async saveChanges() {
      this.loading = true
      try {
        // Save all changed items
        const changedItems = this.scheduleItems.filter(item => {
          const original = this.originalScheduleItems.find(
            o => o.id === item.id
          )
          if (!original) return false
          return (
            item.start_time !== original.start_time ||
            item.end_time !== original.end_time
          )
        })

        for (const item of changedItems) {
          await ScheduleApi.update({
            id: item.id,
            start_time: item.start_time,
            end_time: item.end_time,
            media_id: item.media_id,
            episode_id: item.episode_id,
            channel_id: this.channelId,
            repeats_id: this.repeatsId
          })
          this.$set(item, 'editing', false)
        }

        // Update original values
        this.originalScheduleItems = JSON.parse(
          JSON.stringify(this.scheduleItems)
        )

        this.$buefy.toast.open({
          message: 'Changes saved successfully!',
          position: 'is-top',
          type: 'is-success'
        })

        // Refresh the calendar
        this.$emit('refresh')
      } catch (error) {
        this.$buefy.toast.open({
          message: 'Failed to save changes',
          position: 'is-top',
          type: 'is-danger'
        })
      } finally {
        this.loading = false
      }
    }
  }
}
</script>
